import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export interface SEOProps {
  title?: string;
  description?: string;
  noIndex?: boolean;
  type?: 'website' | 'article';
  image?: string;
  jsonLd?: Record<string, any>;
  canonicalPath?: string; // Optional override
}

const DEFAULT_TITLE = 'Audit-Ready Evidence for Software Changes | EvidentTrail';
const DEFAULT_DESCRIPTION = 'Read-only GitHub change control evidence covering approvals, drift detection, and exception tracking for SOC 2 and ISO 27001 audits.';
const SITE_NAME = 'EvidentTrail';
const BASE_URL = 'https://evidenttrail.com';
const DEFAULT_IMAGE = 'https://evidenttrail.com/og-image.jpg'; // TODO: Update with real asset

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  noIndex = false,
  type = 'website',
  image = DEFAULT_IMAGE,
  jsonLd,
  canonicalPath
}: SEOProps) {
  const { pathname } = useLocation();
  // Strip trailing slash for consistency, unless root
  const cleanPath = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const canonicalUrl = `${BASE_URL}${canonicalPath || cleanPath}`;

  const pageTitle = title
    ? (title.includes('|') ? title : `${title} | EvidentTrail`) // Avoid double branding
    : DEFAULT_TITLE;

  // Default Organization Schema
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "EvidentTrail",
    "applicationCategory": "DevOps",
    "operatingSystem": "Cloud",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd || defaultSchema)}
      </script>
    </Helmet>
  );
}
